import { ComponentType, FC, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";

import { SpacingToken } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { config } from "config/config";
import { SubscriptionPhase } from "constants/subscription-phase";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { isInIframe } from "helpers/app";
import { AgentsRoutes } from "routes/agents/constants";
import { SettingsRoutes } from "routes/settings/constants";
import { trackAddDomainModalOpened } from "routes/settings/inboxes/domains/analytics";
import { EmailSettingsRoutes } from "routes/settings/inboxes/routes";
import { AgentActions, selectHasInvitedOtherAgent, selectHasSeenOnboardingChecklist } from "store/entities/agents";
import { selectHasDomains } from "store/entities/email-domains/selectors";
import {
  selectHasDoneLiveChatSelectiveImportBefore,
  selectHasEverDoneFullLiveChatDataImport,
  selectHasLiveChatIntegration,
  selectHasLivechatAgentsImported,
} from "store/entities/integrations";
import { selectMessagingModeEnabled } from "store/entities/license";
import { selectSubscriptionPhase } from "store/entities/subscriptions";
import { selectTicketsForwardedToHelpDesk } from "store/views/tickets/selectors";
import { withVar } from "themes/helpers";

import { Drawer } from "../Drawer";

import { OnboardingChecklist, OnboardingStep, OnboardingStepItem } from "./OnboardingChecklist";
import { OnboardingNextSteps } from "./OnboardingNextSteps";
import { TrialDaysAlert } from "./TrialDaysAlert";

interface IProps {
  openOnInit?: boolean;
  trigger: ComponentType<{ stepsCompleted: number; stepsToComplete: number; onDrawerOpen: VoidFunction }>;
}

export const OnboardingDrawer: FC<IProps> = ({ openOnInit = false, trigger: Trigger }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const appInIframe = isInIframe();

  const hasLivechatIntegration = useSelector(selectHasLiveChatIntegration);
  const messagingModeEnabled = useSelector(selectMessagingModeEnabled);
  const selectiveImportCompleted = useSelector(selectHasDoneLiveChatSelectiveImportBefore);
  const hasEverDoneFullLiveChatDataImport = useSelector(selectHasEverDoneFullLiveChatDataImport);
  const hasLivechatAgentsImported = useSelector(selectHasLivechatAgentsImported);
  const ticketsForwarded = useSelector(selectTicketsForwardedToHelpDesk);
  const hasInvitedOtherAgent = useSelector(selectHasInvitedOtherAgent);
  const isTrialWithoutCC = useSelector(selectSubscriptionPhase) === SubscriptionPhase.TrialNoCC;
  const hasNeverSeenDrawerBefore = !useSelector(selectHasSeenOnboardingChecklist);
  const hasDomains = useSelector(selectHasDomains);

  const [isDrawerOpened, setIsDrawerOpened] = useState(openOnInit || hasNeverSeenDrawerBefore);

  const openDrawer = (): void => {
    setIsDrawerOpened(true);
    trackEngagement("Checklist: Open");
  };

  const closeDrawer = (): void => {
    setIsDrawerOpened(false);
    trackEngagement("Checklist: Close");
    if (hasNeverSeenDrawerBefore) {
      dispatch(
        AgentActions.setFlag({ name: "hasSeenOnboardingChecklist_6a7ad10d540a00bab25beca7650b2c77", value: true })
      );
    }
  };

  const handleItemClick = (path: string, step: string): void => {
    trackEngagement("Checklist: Redirect links clicked", EventPlace.Onboarding, {
      link: step,
    });
    if (appInIframe) {
      window.open(`${config.appUrl}${path}`);
    } else {
      history.push(path);
      closeDrawer();
    }
  };

  const stepsToComplete = hasLivechatIntegration
    ? [
        OnboardingStep.InstallHelpDesk,
        OnboardingStep.ImportData,
        OnboardingStep.ForwardEmails,
        OnboardingStep.SetUpDomain,
        OnboardingStep.InviteTeammates,
        OnboardingStep.AllowLeavingTickets,
      ]
    : [
        OnboardingStep.InstallHelpDesk,
        OnboardingStep.ForwardEmails,
        OnboardingStep.SetUpDomain,
        OnboardingStep.InviteTeammates,
      ];

  const allStepItems: Record<OnboardingStep, OnboardingStepItem> = {
    [OnboardingStep.InstallHelpDesk]: {
      id: OnboardingStep.InstallHelpDesk,
      title: (
        <>
          <mark>Create</mark> the HelpDesk account
        </>
      ),
      completed: true,
    },
    [OnboardingStep.ImportData]: {
      id: OnboardingStep.ImportData,
      title: (
        <>
          <mark>Import</mark> data from LiveChat
        </>
      ),
      completed: hasEverDoneFullLiveChatDataImport || selectiveImportCompleted,
      onClick: () => handleItemClick(`${SettingsRoutes.Apps}/livechat`, "import"),
    },
    [OnboardingStep.ForwardEmails]: {
      id: OnboardingStep.ForwardEmails,
      title: (
        <>
          <mark>Set up</mark> email forwarding to get tickets
        </>
      ),
      completed: ticketsForwarded,
      onClick: () => handleItemClick(EmailSettingsRoutes.ForwardingAddressesWithInstructions, "forwarding"),
    },
    [OnboardingStep.SetUpDomain]: {
      id: OnboardingStep.SetUpDomain,
      title: (
        <>
          <mark>Set up</mark> your domain
        </>
      ),
      completed: hasDomains,
      onClick: () => {
        trackAddDomainModalOpened("onboarding_checklist");
        handleItemClick(EmailSettingsRoutes.DomainsWithModal, "domain");
      },
    },
    [OnboardingStep.InviteTeammates]: {
      id: OnboardingStep.InviteTeammates,
      title: (
        <>
          <mark>Invite</mark> teammates to cooperate
        </>
      ),
      completed: hasInvitedOtherAgent || hasLivechatAgentsImported,
      onClick: () => handleItemClick(`${AgentsRoutes.Agents}/invite`, "invite"),
    },

    [OnboardingStep.AllowLeavingTickets]: {
      id: OnboardingStep.AllowLeavingTickets,
      title: (
        <>
          <mark>Allow</mark> ticket creation when chat is inactive
        </>
      ),
      completed: !messagingModeEnabled,
      href: `${config.livechatAgentAppHost}/settings/availability`,
      target: "_blank",
      onClick: () => {
        trackEngagement("Checklist: Redirect links clicked", EventPlace.Onboarding, {
          link: "availability",
        });
      },
    },
  };

  const stepItemsToComplete = stepsToComplete.map(step => allStepItems[step]);

  const stepsCompleted = stepItemsToComplete.reduce(
    (completedSteps, step) => (step.completed ? completedSteps + 1 : completedSteps),
    0
  );

  return (
    <>
      <Trigger stepsCompleted={stepsCompleted} stepsToComplete={stepsToComplete.length} onDrawerOpen={openDrawer} />
      <Drawer opened={isDrawerOpened} onClose={closeDrawer}>
        <Flex column gap={`${withVar(SpacingToken.Spacing6)}`}>
          {isTrialWithoutCC && <TrialDaysAlert onClose={closeDrawer} />}
          <OnboardingChecklist stepsCompleted={stepsCompleted} stepItems={stepItemsToComplete} />
          <OnboardingNextSteps />
        </Flex>
      </Drawer>
    </>
  );
};
